import { http, HttpResponse } from 'msw'
import type { 
    BindLinkRequest, 
    BindLinkResponse, 
    GetLinkStatusResponse,
    SharingRequestStatuses
} from '@/types/api'

// Mock data store to simulate server state
const mockData: Record<string, {
    status: SharingRequestStatuses
    email?: string
    partner_code?: string
    createdAt: Date
}> = {
    // Test link IDs for different scenarios
    'valid-init-link': {
        status: 'init' as SharingRequestStatuses,
        createdAt: new Date()
    },
    'valid-linked-link': {
        status: 'linked' as SharingRequestStatuses,
        email: 'test@example.com',
        createdAt: new Date()
    },
    'expired-link': {
        status: 'expired' as SharingRequestStatuses,
        createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000) // 1 day ago
    },
    'verified-link': {
        status: 'verified' as SharingRequestStatuses,
        email: 'verified@example.com',
        createdAt: new Date()
    },
    '1488': {
        status: 'init' as SharingRequestStatuses,
        createdAt: new Date()
    },
    'request-in-progress-link': {
        status: 'init' as SharingRequestStatuses,
        createdAt: new Date()
    },
    // Test partner codes that can work as lookup keys
    'BANK001': {
        status: 'init' as SharingRequestStatuses,
        partner_code: 'BANK001',
        createdAt: new Date()
    },
    'MOBILE001': {
        status: 'init' as SharingRequestStatuses,
        partner_code: 'MOBILE001',
        createdAt: new Date()
    }
}

export const handlers = [
    // POST /api/mobile/v1/residency/bind-request - Submit email for bind request
    http.post<never, BindLinkRequest>('*/api/mobile/v1/residency/bind-request', async ({ request }) => {
        console.log('MSW: Handling POST /api/mobile/v1/residency/bind-request')
        const body = await request.json()
        const { link_id, email, partner_code } = body
        console.log('MSW: Request body:', { link_id, email, partner_code })

        // Validate request - either link_id or partner_code is required
        if ((!link_id && !partner_code) || !email) {
            return HttpResponse.json(
                { error: 'Missing required fields (need either link_id or partner_code, plus email)' },
                { status: 400 }
            )
        }

        // Determine lookup key - prefer link_id, fallback to partner_code
        const lookupKey = link_id || partner_code

        // TypeScript guard: ensure lookupKey is not undefined
        if (!lookupKey) {
            return HttpResponse.json(
                { error: 'Missing lookup key' },
                { status: 400 }
            )
        }

        // Simulate request_in_progress error for special test link
        if (lookupKey === 'request-in-progress-link') {
            return HttpResponse.json(
                {
                    code: 'request_in_progress',
                    message: 'Request is already being processed'
                },
                { status: 403 }
            )
        }

        // Check if link exists
        if (!mockData[lookupKey]) {
            return HttpResponse.json(
                { error: 'Link not found' },
                { status: 200 }
            )
        }

        const linkData = mockData[lookupKey]

        // Check if link is in valid state for binding
        if (linkData.status !== 'init') {
            return HttpResponse.json(
                { error: 'Link is not in valid state for binding' },
                { status: 400 }
            )
        }

        // Check if link is expired (simulate expiration after 1 hour)
        const isExpired = Date.now() - linkData.createdAt.getTime() > 60 * 60 * 1000
        if (isExpired) {
            mockData[lookupKey].status = 'expired' as SharingRequestStatuses
            return HttpResponse.json(
                { error: 'Link has expired' },
                { status: 400 }
            )
        }

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(email)) {
            return HttpResponse.json(
                { error: 'Invalid email format' },
                { status: 400 }
            )
        }

        // Update link status and bind email
        mockData[lookupKey] = {
            ...linkData,
            status: 'linked' as SharingRequestStatuses,
            email,
            partner_code: partner_code || linkData.partner_code
        }

        const response: BindLinkResponse = {
            result: true
        }

        // Simulate network delay
        await new Promise(resolve => setTimeout(resolve, 500))

        return HttpResponse.json(response)
    }),

    // GET /api/mobile/v1/residency/bind-request/status - Get link status
    http.get('*/api/mobile/v1/residency/bind-request/status', ({ request }) => {
        const url = new URL(request.url)
        const link_id = url.searchParams.get('link_id')
        console.log('MSW: link_id parameter:', link_id)

        // Validate request
        if (!link_id) {
            return HttpResponse.json(
                { error: 'Missing link_id parameter' },
                { status: 400 }
            )
        }

        // Simulate request_in_progress error for special test link
        if (link_id === 'request-in-progress-link') {
            return HttpResponse.json(
                {
                    code: 'request_in_progress',
                    message: 'Request is already being processed'
                },
                { status: 403 }
            )
        }

        // Check if link exists
        if (!mockData[link_id]) {
            return HttpResponse.json(
                { error: 'Link not found' },
                { status: 404 }
            )
        }

        const linkData = mockData[link_id]

        // Check for expiration and update status if needed
        const isExpired = Date.now() - linkData.createdAt.getTime() > 60 * 60 * 1000
        if (isExpired && linkData.status !== 'expired') {
            mockData[link_id].status = 'expired' as SharingRequestStatuses
        }

        const response: GetLinkStatusResponse = {
            status: mockData[link_id].status
        }

        return HttpResponse.json(response)
    }),

    // Fallback handler for unmatched requests
    http.all('*/api/*', ({ request }) => {
        console.warn(`Unhandled ${request.method} request to ${request.url}`)
        return HttpResponse.json(
            { error: 'Not found' },
            { status: 404 }
        )
    })
]